import React, { useState, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, DownloadIcon, ExternalLinkIcon, UploadIcon, TrashIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { useFirestoreCollection } from '../hooks/useFirestore';
interface PDFDocument {
  id?: string;
  name: string;
  url: string;
  uploadedAt: string;
}
export function Regelgrundlag() {
  const [isUploading, setIsUploading] = useState(false);
  // Firebase Firestore hook for documents
  const {
    data: documents,
    add: addDocument,
    remove: removeDocument
  } = useFirestoreCollection<PDFDocument>('regelgrundlag');
  const handleOpenPDF = (url: string) => {
    window.open(url, '_blank');
  };
  const handleDownloadPDF = (url: string, name: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleUploadPDF = async () => {
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.multiple = false;
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Validate file type
      if (file.type !== 'application/pdf') {
        alert('Kun PDF filer er tilladt!');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Filen er for stor! Maksimal størrelse er 10MB.');
        return;
      }
      setIsUploading(true);
      try {
        // Upload to Firebase Storage
        const timestamp = Date.now();
        const fileName = `${timestamp}-${file.name}`;
        const storageRef = ref(storage, `regelgrundlag/${fileName}`);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);
        // Save metadata to Firestore
        await addDocument({
          name: file.name,
          url: downloadUrl,
          uploadedAt: new Date().toISOString()
        });
        alert(`"${file.name}" er uploadet!`);
      } catch (error) {
        console.error('Upload error:', error);
        alert('Fejl ved upload af fil. Prøv igen.');
      } finally {
        setIsUploading(false);
      }
    };
    input.click();
  };
  const handleDeletePDF = async (doc: PDFDocument) => {
    if (!window.confirm(`Er du sikker på at du vil slette "${doc.name}"?`)) return;
    try {
      // Extract file path from URL
      const urlParts = doc.url.split('/o/');
      if (urlParts.length > 1) {
        const pathPart = urlParts[1].split('?')[0];
        const filePath = decodeURIComponent(pathPart);
        // Delete from Firebase Storage
        const storageRef = ref(storage, filePath);
        await deleteObject(storageRef);
      }
      // Delete from Firestore
      if (doc.id) {
        await removeDocument(doc.id);
      }
      alert(`"${doc.name}" er slettet!`);
    } catch (error) {
      console.error('Delete error:', error);
      alert('Fejl ved sletning af fil. Prøv igen.');
    }
  };
  return <div className="space-y-6" data-id="element-2297" data-mp="2kv5fwtdp58vx">
      <header className="flex items-center justify-between" data-id="element-2298" data-mp="2kvyfnae8my4k">
        <div data-id="element-2299" data-mp="3iidue4fa5hof">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-2300" data-mp="3ij73hc1poj9q">
            Regelgrundlag
          </h1>
          <p className="text-stone-600" data-id="element-2301" data-mp="3djb11h8nwzwa">
            Vigtige dokumenter og regler for elinstallationer
          </p>
        </div>
        <Button onClick={handleUploadPDF} disabled={isUploading} className="flex items-center gap-2" data-id="element-2302" data-mp="38iwqnsjl9dv3">
          <UploadIcon className="w-5 h-5" data-id="element-2303" data-mp="1x5az60q9b06e" />
          {isUploading ? 'Uploader...' : 'Upload PDF'}
        </Button>
      </header>

      {/* Documents Grid */}
      {documents.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2304" data-mp="3h0fa8kzi3nje">
          {documents.map((doc, index) => <motion.div key={doc.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.1
      }} className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2305" data-mp="3lrdqxloeinn6" data-mpi={index}>
              {/* PDF Icon */}
              <div className="flex items-center justify-center w-16 h-16 bg-red-50 rounded-xl mb-4" data-id="element-2306" data-mp="3pszafpvfy06c">
                <FileTextIcon className="w-8 h-8 text-red-600" data-id="element-2307" data-mp="3urk1ayosz7i4" />
              </div>

              {/* Document Info */}
              <h3 className="text-lg font-semibold text-stone-900 mb-2 line-clamp-2" data-id="element-2308" data-mp="2edal4rupsp2d">
                {doc.name}
              </h3>
              <p className="text-sm text-stone-500 mb-4" data-id="element-2309" data-mp="2jdydphylddyp">
                Uploadet:{' '}
                {new Date(doc.uploadedAt).toLocaleDateString('da-DK', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
              </p>

              {/* Actions */}
              <div className="flex gap-2" data-id="element-2310" data-mp="16e4wals54c7i">
                <button onClick={() => handleOpenPDF(doc.url)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors" data-id="element-2311" data-mp="16f66yp58uwop">
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-2312" data-mp="rguderd6441r" />
                  Åbn
                </button>
                <button onClick={() => handleDownloadPDF(doc.url, doc.name)} className="flex items-center justify-center px-4 py-2 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors" title="Download" data-id="element-2313" data-mp="mqdz751iweec">
                  <DownloadIcon className="w-4 h-4" data-id="element-2314" data-mp="1u3zjmkwof5dx" />
                </button>
                <button onClick={() => handleDeletePDF(doc)} className="flex items-center justify-center px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors" title="Slet" data-id="element-2315" data-mp="1u1n1o7qx4v0v">
                  <TrashIcon className="w-4 h-4" data-id="element-2316" data-mp="28s2qdwijml3s" />
                </button>
              </div>
            </motion.div>)}
        </div>}

      {/* Empty State */}
      {documents.length === 0 && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="bg-white rounded-2xl p-12 border border-stone-200 text-center" data-id="element-2317" data-mp="1g46w0ygg55a6">
          <FileTextIcon className="w-16 h-16 text-stone-300 mx-auto mb-4" data-id="element-2318" data-mp="2wi6qvv0fq4qk" />
          <h3 className="text-xl font-semibold text-stone-900 mb-2" data-id="element-2319" data-mp="2wkauebuf91qb">
            Ingen dokumenter endnu
          </h3>
          <p className="text-stone-500 mb-6" data-id="element-2320" data-mp="pty2chbe4ky6">
            Upload PDF filer med vigtige regler og dokumenter
          </p>
          <Button onClick={handleUploadPDF} disabled={isUploading} className="flex items-center gap-2 mx-auto" data-id="element-2321" data-mp="kubhpmt8g2fc">
            <UploadIcon className="w-5 h-5" data-id="element-2322" data-mp="g21vohv8nmic" />
            {isUploading ? 'Uploader...' : 'Upload første PDF'}
          </Button>
        </motion.div>}

      {/* Info Box */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-blue-50 border border-blue-200 rounded-2xl p-6" data-id="element-2323" data-mp="67i5wrix9erw">
        <h3 className="text-lg font-semibold text-blue-900 mb-2" data-id="element-2324" data-mp="3ocv6gfcpcq5e">
          📋 Om Regelgrundlag
        </h3>
        <p className="text-sm text-blue-700 mb-3" data-id="element-2325" data-mp="1riruvh18ve2g">
          Her kan du gemme og få adgang til vigtige dokumenter som
          elinstallationsreglementet, stærkstrømsbekendtgørelsen, og andre
          relevante sikkerhedsforskrifter. Alle dokumenter er tilgængelige for
          hele teamet.
        </p>
        <ul className="text-sm text-blue-700 space-y-1" data-id="element-2326" data-mp="10d5wnj3l5rwj">
          <li data-id="element-2327" data-mp="lpt9jpy4re41">✓ Maksimal filstørrelse: 10MB</li>
          <li data-id="element-2328" data-mp="lmyopxb7fra3">✓ Kun PDF filer tilladt</li>
          <li data-id="element-2329" data-mp="qnmi2pmwok7h">✓ Automatisk synkronisering med Firebase</li>
        </ul>
      </motion.div>
    </div>;
}